// Cross-module HR insights (Phase 6a) — a single dashboard pulling from
// workforce, recruitment, learning, engagement and employee-relations data.
import { useState, useEffect } from 'react';
import { Users, Briefcase, GraduationCap, Award, ShieldAlert, Inbox, Timer } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, Pill } from './ui';
import { AG } from './theme/tokens';
import { api } from './api';

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <Card style={{ padding: 16 }} className="flex items-center gap-3">
      <div style={{ width: 42, height: 42, borderRadius: 12, background: AG.primaryLightest, color: AG.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon size={20} /></div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: AG.text, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 12, color: AG.n500, marginTop: 3 }}>{label}{sub ? <span style={{ color: AG.n400 }}> · {sub}</span> : null}</div>
      </div>
    </Card>
  );
}

function Bars({ title, groups }) {
  const max = Math.max(1, ...groups.map((g) => g.count));
  return (
    <Card style={{ padding: 18 }}>
      <div style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>{title}</div>
      {groups.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>No data.</p> : (
        <div style={{ display: 'grid', gap: 8 }}>
          {groups.map((g) => (
            <div key={g.key}>
              <div className="flex items-center justify-between" style={{ fontSize: 13, marginBottom: 3 }}>
                <span style={{ color: AG.text }}>{g.key}</span>
                <span style={{ color: AG.n500 }}>{g.count} · {g.pct}%</span>
              </div>
              <div style={{ height: 7, borderRadius: 6, background: AG.n100, overflow: 'hidden' }}>
                <div style={{ width: `${Math.round((g.count / max) * 100)}%`, height: '100%', background: AG.primary }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function SlaPanel({ sla }) {
  if (!sla) return null;
  const t = sla.totals;
  return (
    <Card style={{ padding: 18 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <div className="flex items-center gap-2"><Timer size={17} style={{ color: AG.primary }} /><span style={{ fontWeight: 600, color: AG.text }}>Approval SLAs</span></div>
        <Pill tone={t.breached ? 'error' : 'success'}>{t.breached}/{t.pending} breached</Pill>
      </div>
      {sla.groups.length === 0 ? <p style={{ fontSize: 13, color: AG.n500 }}>No pending approvals.</p> : (
        <div style={{ display: 'grid', gap: 8 }}>
          {sla.groups.map((g) => (
            <div key={g.workflowKey} className="flex items-center justify-between" style={{ borderBottom: `1px solid ${AG.divider}`, paddingBottom: 8 }}>
              <span style={{ fontSize: 13.5, color: AG.text, textTransform: 'capitalize' }}>{g.workflowKey}</span>
              <div className="flex items-center gap-2">
                <span style={{ fontSize: 12, color: AG.n500 }}>{g.onTrack} on track · {g.pending} pending</span>
                <Pill tone={g.breached ? 'error' : 'success'}>{g.breached} late ({g.breachRate}%)</Pill>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function OutcomesPanel({ outcomes }) {
  if (!outcomes) return null;
  return (
    <Card style={{ padding: 18 }}>
      <div style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>Programme outcomes vs charter targets</div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {outcomes.kpis.map((k) => {
          const hasTarget = k.target != null;
          const ratio = hasTarget ? Math.min(100, Math.round((k.value / k.target) * 100)) : 100;
          const tone = k.met === true ? AG.success : k.met === false ? AG.warning : AG.primary;
          return (
            <div key={k.key}>
              <div className="flex items-center justify-between" style={{ fontSize: 13, marginBottom: 3 }}>
                <span style={{ color: AG.text, fontWeight: 600 }}>{k.label}</span>
                <span style={{ color: AG.n600 }}>
                  {k.value == null ? '—' : `${k.value}${k.unit}`}{hasTarget ? <span style={{ color: AG.n400 }}> / {k.target}{k.unit} target</span> : null}
                  {k.met === true && <span style={{ marginLeft: 6 }}><Pill tone="success">met</Pill></span>}
                </span>
              </div>
              <div style={{ height: 7, borderRadius: 6, background: AG.n100, overflow: 'hidden' }}>
                <div style={{ width: `${k.value == null ? 0 : ratio}%`, height: '100%', background: tone }} />
              </div>
              <div style={{ fontSize: 11, color: AG.n400, marginTop: 4 }}>{k.basis}</div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function WorkforcePanel({ w }) {
  if (!w) return null;
  const max = Math.max(1, ...w.tenure.map((t) => t.count));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card style={{ padding: 18 }}>
        <div style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>Turnover & retention</div>
        <div className="grid grid-cols-3 gap-3">
          {[['Turnover', `${w.turnoverRate}%`], ['Exits', w.exits], ['Avg tenure', `${w.avgTenure} yr`]].map(([l, v]) => (
            <div key={l}><div style={{ fontSize: 20, fontWeight: 700, color: AG.text }}>{v}</div><div style={{ fontSize: 11, color: AG.n500 }}>{l}</div></div>
          ))}
        </div>
      </Card>
      <Card style={{ padding: 18 }}>
        <div style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>Tenure distribution</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {w.tenure.map((t) => (
            <div key={t.bucket}>
              <div className="flex items-center justify-between" style={{ fontSize: 13, marginBottom: 3 }}><span style={{ color: AG.text }}>{t.bucket}</span><span style={{ color: AG.n500 }}>{t.count}</span></div>
              <div style={{ height: 7, borderRadius: 6, background: AG.n100, overflow: 'hidden' }}><div style={{ width: `${Math.round((t.count / max) * 100)}%`, height: '100%', background: AG.primary }} /></div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function DepartmentsPanel({ rows }) {
  if (!rows || !rows.length) return null;
  return (
    <Card style={{ padding: 18 }}>
      <div style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>By department</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: AG.n500 }}>
              {['Department', 'Headcount', 'Managers', 'Avg OKR', 'Pending leave'].map((h, i) => (
                <th key={h} style={{ padding: '6px 10px', textAlign: i === 0 ? 'left' : 'right', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.dept} style={{ borderTop: `1px solid ${AG.divider}` }}>
                <td style={{ padding: '8px 10px', color: AG.text }}>{r.dept}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: AG.text }}>{r.headcount}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: AG.n500 }}>{r.managers}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}><Pill tone={r.avgOkrProgress >= 70 ? 'success' : r.avgOkrProgress >= 40 ? 'warning' : 'neutral'}>{r.avgOkrProgress}%</Pill></td>
                <td style={{ padding: '8px 10px', textAlign: 'right', color: r.pendingLeaves ? AG.warning : AG.n400 }}>{r.pendingLeaves}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function RecruitmentPanel({ r }) {
  if (!r) return null;
  const max = Math.max(1, ...r.funnel.map((f) => f.count));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card style={{ padding: 18 }}>
        <div style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>Hiring funnel</div>
        <div className="grid grid-cols-3 gap-3" style={{ marginBottom: 14 }}>
          {[['Avg time-to-hire', r.avgTimeToHire == null ? '—' : `${r.avgTimeToHire}d`], ['Offer acceptance', r.offerAcceptanceRate == null ? '—' : `${r.offerAcceptanceRate}%`], ['Hired', r.hired]].map(([l, v]) => (
            <div key={l}><div style={{ fontSize: 20, fontWeight: 700, color: AG.text }}>{v}</div><div style={{ fontSize: 11, color: AG.n500 }}>{l}</div></div>
          ))}
        </div>
        <div style={{ display: 'grid', gap: 8 }}>
          {r.funnel.map((f) => (
            <div key={f.stage}>
              <div className="flex items-center justify-between" style={{ fontSize: 13, marginBottom: 3 }}><span style={{ color: AG.text }}>{f.stage}</span><span style={{ color: AG.n500 }}>{f.count}</span></div>
              <div style={{ height: 7, borderRadius: 6, background: AG.n100, overflow: 'hidden' }}><div style={{ width: `${Math.round((f.count / max) * 100)}%`, height: '100%', background: AG.primary }} /></div>
            </div>
          ))}
        </div>
      </Card>
      <Card style={{ padding: 18 }}>
        <div style={{ fontWeight: 600, color: AG.text, marginBottom: 12 }}>Candidate sources</div>
        {r.bySource.length === 0 ? <div style={{ fontSize: 13, color: AG.n500 }}>No candidates yet.</div> : (
          <div style={{ display: 'grid', gap: 8 }}>
            {r.bySource.map((s) => {
              const smax = Math.max(1, ...r.bySource.map((x) => x.count));
              return (
                <div key={s.source}>
                  <div className="flex items-center justify-between" style={{ fontSize: 13, marginBottom: 3 }}><span style={{ color: AG.text }}>{s.source}</span><span style={{ color: AG.n500 }}>{s.count}</span></div>
                  <div style={{ height: 7, borderRadius: 6, background: AG.n100, overflow: 'hidden' }}><div style={{ width: `${Math.round((s.count / smax) * 100)}%`, height: '100%', background: AG.success || '#12B76A' }} /></div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

export default function Insights() {
  const [d, setD] = useState(null);
  const [sla, setSla] = useState(null);
  const [outcomes, setOutcomes] = useState(null);
  const [workforce, setWorkforce] = useState(null);
  const [departments, setDepartments] = useState(null);
  const [recruitment, setRecruitment] = useState(null);
  useEffect(() => {
    api.insightsOverview().then(setD).catch(() => setD(null));
    api.slaOverview().then(setSla).catch(() => setSla(null));
    api.outcomesReport().then(setOutcomes).catch(() => setOutcomes(null));
    api.workforceAnalytics().then(setWorkforce).catch(() => setWorkforce(null));
    api.departmentAnalytics().then(setDepartments).catch(() => setDepartments(null));
    api.recruitmentAnalytics().then(setRecruitment).catch(() => setRecruitment(null));
  }, []);
  if (!d) return <CenterSpinner />;

  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="Insights" sub="Cross-module HR analytics" />

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Stat icon={Users} label="Headcount" value={d.workforce.total} sub={`${d.workforce.active} active`} />
        <Stat icon={Briefcase} label="Candidates" value={d.recruitment.candidates} sub={`${d.recruitment.jobs} jobs`} />
        <Stat icon={GraduationCap} label="Course completion" value={`${d.learning.completionRate}%`} sub={`${d.learning.completed}/${d.learning.enrollments}`} />
        <Stat icon={Award} label="Recognitions" value={d.engagement.recognitions} sub={`${d.engagement.points} pts`} />
        <Stat icon={ShieldAlert} label="Open cases" value={d.relations.openCases} />
        <Stat icon={Inbox} label="Pending approvals" value={d.approvals.pending} sub={`${d.approvals.leaves} leave · ${d.approvals.requests} req`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1"><Bars title="By department" groups={d.workforce.byDept} /></div>
        <div className="lg:col-span-1"><Bars title="By employment type" groups={d.workforce.byType} /></div>
        <div className="lg:col-span-1"><Bars title="By gender" groups={d.workforce.byGender} /></div>
      </div>

      <WorkforcePanel w={workforce} />
      <DepartmentsPanel rows={departments} />
      <RecruitmentPanel r={recruitment} />
      <OutcomesPanel outcomes={outcomes} />
      <SlaPanel sla={sla} />
    </div>
  );
}
